import { MidGrid } from "../../styles/emit-styles/Emit-styles";
import CardGrid from "./CardGrid";
import DistributionCard from "./DistributionCard";
import GraphCard from "./GraphCard";
import { compileAltFuelCompositionCardData, compileAvoidedEmissionsByDrivetrainData, compileAvoidedEmissionsViewGraphData, compileDistanceBreakdownByAltDrivetrainData, formatData, getEmissionsUnits } from "./UtilityFunctions";
import { altDrivetrains, combinedEmissionsBars, emissionsByDrivetrainBars, AVOIDED_TABLE_TYPE } from './HelperConstants';
import TableCard from "./TableCard";
import { useEffect, useState } from "react";
import { formatNumber } from "./UtilityFunctions";


const AvoidedEmissionsView = ({ summaryData, dbDisplayName, beginDate, endDate, selectedVehicleClasses, vehicleClasses, tableData, graphData, defaultToMonthView, isTd, methodology, selectedGroups, macroScale, metricUnitPrefs, userSettings }) => {
    const FINE_PM = formatNumber(2.5, userSettings);
    
    const [viewDataByMonth, setViewDataByMonth] = useState(defaultToMonthView);
    const [avoidedEmissionsData, setAvoidedEmissionsData] = useState([]);
    const [showDiscreteData, setShowDiscreteData] = useState(false);
    const [splitByDrivetrain, setSplitByDrivetrain] = useState(false);
    const [drivetrainsWithData, setDrivetrainsWithData] = useState({});

    useEffect(() => {
        setAvoidedEmissionsData([]); // wipe graph data array

        setDrivetrainsWithData(splitByDrivetrain ?
            {
                "bevAvoided": summaryData.bevGhgAvoided !== 0,
                "phevAvoided": summaryData.phevGhgAvoided !== 0,
                "cngAvoided": summaryData.cngGhgAvoided !== 0,
            } : {
                "avoided": summaryData.ghgAvoided !== 0,
            }
        );

        const updatedGraphData = compileAvoidedEmissionsViewGraphData(graphData, viewDataByMonth, splitByDrivetrain, showDiscreteData);
        setAvoidedEmissionsData(updatedGraphData);
    }, [graphData, summaryData, viewDataByMonth, showDiscreteData, splitByDrivetrain]);

    const emissionsCardData = ((methodology?.value !== 'moves3') ?
        [
            {
                value: formatData('ghg', summaryData.ghgAvoided, userSettings),
                label: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false),
                caption: "Avoided GHG Emissions",
                subcaption: "(Including GHG from kWh gen.)",
                id: "generated-ghg-card"
            },
            {
                value: formatData('co2', summaryData.co2Avoided, userSettings),
                label: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Carbon Dioxide (CO<sub>2</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-co2-card"
            },
            {
                value: formatData('ch4', summaryData.ch4Avoided, userSettings),
                label: getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('ch4', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Methane (CH<sub>4</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-co-card"
            },
            {
                value: formatData('nox', summaryData.noxAvoided, userSettings),
                label: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Nitrous Oxide (NO<sub>x</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-nox-card"
            },
            {
                value: formatData('bioco2', summaryData.bioco2Avoided, userSettings),
                label: getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('bioco2', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Biofuel CO<sub>2</sub> (BioCO<sub>2</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-biofuel-card"
            }
        ]
        :
        [
            {
                value: formatData('ghg', summaryData.ghgAvoided, userSettings),
                label: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false),
                caption: "GHG Emissions",
                subcaption: "(Including GHG from kWh gen.)",
                id: "avoided-ghg-card"
            },
            {
                value: formatData('co2', summaryData.co2Avoided, userSettings),
                label: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('co2', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Carbon Dioxide (CO<sub>2</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-co2-card"
            },
            {
                value: formatData('co', summaryData.coAvoided, userSettings),
                label: getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('co', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Carbon Monoxide (CO)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-methane-card"
            },
            {
                value: formatData('nox', summaryData.noxAvoided, userSettings),
                label: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('nox', metricUnitPrefs.emissions, macroScale, false),
                caption: <div>Nitrous Oxide (NO<sub>x</sub>)</div>,
                subcaption: "(Tailpipe)",
                id: "avoided-nox-card"
            },
            {
                value: formatData('pm10', summaryData.pm10Avoided, userSettings),
                label: getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('pm10', metricUnitPrefs.emissions, macroScale, false),
                caption: "Particulate Matter 10\u00B5m (PM 10\u00B5m)",
                subcaption: "(Tailpipe)",
                id: "avoided-pm10-card"
            },
            {
                value: formatData('pm25', summaryData.pm25Avoided, userSettings),
                label: getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, true),
                labelHover: getEmissionsUnits('pm25', metricUnitPrefs.emissions, macroScale, false),
                caption: `Particulate Matter ${FINE_PM}\u00B5m (PM ${FINE_PM}\u00B5m)`,
                subcaption: "(Tailpipe)",
                id: "avoided-pm25-card"
            },
        ]
    );

    const altFuelCompositionCardData = compileAltFuelCompositionCardData(summaryData);
    const distanceBreakdownByAltDrivetrainData = compileDistanceBreakdownByAltDrivetrainData(summaryData);
    const avoidedEmissionsByDrivetrainData = compileAvoidedEmissionsByDrivetrainData(summaryData);

    const graphUnits = getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false);

    return (
        <>
            <CardGrid
                cardInfoArray={emissionsCardData}
                cardMinWidth="215px"
                cardMaxWidth="100%"
                cardHeight="130px"
                gridLayout="repeat(auto-fit, minmax(215px,1fr))"
            />
            <MidGrid
                columnLayout="2fr minmax(350px, 1fr)"
            >
                <GraphCard
                    height="auto"
                    minWidth="500px"
                    maxWidth="100%"
                    title={`${viewDataByMonth ? "Monthly" : "Weekly"} Avoided Emissions`}
                    subtitle={`${splitByDrivetrain ? "By Fuel Type " : ""}${!showDiscreteData ? "(Cumulative)" : "(Discrete)"}`}
                    data={avoidedEmissionsData}
                    barInfo={splitByDrivetrain ? emissionsByDrivetrainBars : combinedEmissionsBars}
                    yAxisLabel={`GHG (${graphUnits})`}
                    units={graphUnits}
                    xAxisKey="month"
                    showHoverLabels={false}
                    viewDataByMonth={viewDataByMonth}
                    xAxisLabel={viewDataByMonth ? null : 'Week Starting'}
                    showDiscreteData={showDiscreteData}
                    splitByDrivetrain={splitByDrivetrain}
                    setViewDataByMonth={setViewDataByMonth}
                    setShowDiscreteData={setShowDiscreteData}
                    setSplitByDrivetrain={setSplitByDrivetrain}
                    drivetrainsWithData={drivetrainsWithData}
                    metricUnitPrefs={metricUnitPrefs}
                    macroScale={macroScale}
                    userSettings={userSettings}
                />
                <div>
                    <DistributionCard
                        title="Fleet Composition"
                        subtitle="Alternative Fuel Vehicles"
                        height="105px"
                        minWidth="350px"
                        maxWidth="100%"
                        dataTypes={altDrivetrains}
                        data={altFuelCompositionCardData}
                        dataType="altFuelVehicles"
                        colorHoverValues
                        useLongLabels
                        hideLegend
                    />
                    <DistributionCard
                        title="Distance Breakdown"
                        subtitle="By Fuel Type"
                        height="105px"
                        minWidth="350px"
                        maxWidth="100%"
                        dataTypes={altDrivetrains}
                        data={distanceBreakdownByAltDrivetrainData}
                        dataType="altFuelDistance"
                        metricUnitPrefs={metricUnitPrefs}
                        colorHoverValues
                        useLongLabels
                        hideLegend
                        isTd={isTd}
                        userSettings={userSettings}
                    />
                    <DistributionCard
                        title="Avoided Emissions"
                        subtitle="By Fuel Type"
                        height="105px"
                        minWidth="350px"
                        maxWidth="100%"
                        dataTypes={altDrivetrains}
                        data={avoidedEmissionsByDrivetrainData}
                        dataType="avoidedEmissions"
                        metricUnitPrefs={metricUnitPrefs}
                        macroScale={macroScale}
                        colorHoverValues
                        useLongLabels
                        hideLegend
                        userSettings={userSettings}
                    />
                    <DistributionCard
                        height="55px"
                        minWidth="350px"
                        maxWidth="100%"
                        dataTypes={altDrivetrains}
                        data={altFuelCompositionCardData}
                        onlyLegend
                    />
                </div>
            </MidGrid>
            <TableCard
                maxHeight="600px"
                width="auto"
                title="Avoided Emissions Data"
                subtitle="for alternative fuel vehicles"
                tableType={AVOIDED_TABLE_TYPE}
                beginDate={beginDate}
                endDate={endDate}
                tableData={tableData}
                selectedGroups={selectedGroups}
                selectedVehicleClasses={selectedVehicleClasses}
                vehicleClasses={vehicleClasses}
                dbDisplayName={dbDisplayName}
                methodology={methodology}
                isTd={isTd}
                metricUnitPrefs={metricUnitPrefs}
                macroScale={macroScale}
                userSettings={userSettings}
            />
        </>
    )
};

export default AvoidedEmissionsView;






